import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import Select from "react-select";
import {
  FaUser,
  FaPhone,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaRupeeSign,
  FaShoppingCart,
  FaPlusCircle,
  FaTrashAlt,
} from "react-icons/fa";
import Swal from "sweetalert2";
import axios from "axios";
import { baseurl } from "../../baseurl";
import { useParams, useNavigate } from "react-router-dom";

function EditInvoice() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState({
    fullName: "",
    phone: "",
    address: "",
    date: new Date(),
    paymentMode: { value: "cash", label: "Cash" },
    cashAmount: 0,
    upiAmount: 0,
  });

  const [items, setItems] = useState([]);
  const [medicineOptions, setMedicineOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const paymentOptions = [
    { value: "cash", label: "Cash" },
    { value: "upi", label: "UPI" },
    { value: "card", label: "Card" },
    { value: "cash+upi", label: "Cash + UPI" },
  ];

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "10px",
      borderColor: state.isFocused ? "#0d6efd" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(13,110,253,0.2)" : "none",
      "&:hover": { borderColor: "#0d6efd" },
      minHeight: "42px",
    }),
  };

  // ✅ Fetch medicines
  useEffect(() => {
    const fetchMedicines = async () => {
      try {
        const res = await axios.get(`${baseurl}getMedicineSummary`);
        if (res.data.success) {
          const formatted = res.data.data.map((med) => ({
            value: med.id,
            label: med.name,
            selling_price: parseFloat(med.selling_price),
          }));
          setMedicineOptions(formatted);
        }
      } catch (err) {
        console.error("Error fetching medicines:", err);
        Swal.fire("Error", "Failed to load medicine list.", "error");
      }
    };
    fetchMedicines();
  }, []);

  // ✅ Fetch invoice by ID
  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${baseurl}invoices/${id}`);
        if (res.data.success && res.data.data) {
          const inv = res.data.data;
          setInvoice({
            fullName: inv.full_name,
            phone: inv.phone,
            address: inv.address,
            date: new Date(inv.date),
            paymentMode:
              paymentOptions.find((p) => p.value === inv.payment_mode) || {
                value: inv.payment_mode,
                label: inv.payment_mode,
              },
            cashAmount: parseFloat(inv.cash_amount) || 0,
            upiAmount: parseFloat(inv.upi_amount) || 0,
          });

          const formattedItems = inv.items.map((it) => {
  const qty = parseFloat(it.qty) || 0;
  const amt = parseFloat(it.amt) || 0;

  // Use % discount from API if needed, else default 0
  const discount = parseFloat(it.discount) || 0;

  // Recalculate total based on % discount
  const total = qty * amt * (1 - discount / 100);

  return {
    item:
      medicineOptions.find((m) => m.value === it.medicine_id) || {
        value: it.medicine_id,
        label: it.medicine_name,
      },
    qty,
    amt,
    discount: discount,   // keep % only
    total: total,         // recalculated total
  };
});

          setItems(formattedItems);
        }
      } catch (err) {
        console.error("Error fetching invoice:", err);
        Swal.fire("Error", "Failed to load invoice data.", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [id, medicineOptions]);







  // 🧾 Input Handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInvoice({ ...invoice, [name]: value });
  };

  const handleDateChange = (date) => {
    setInvoice({ ...invoice, date: date[0] });
  };

  const handleSelectChange = (selectedOption) => {
    setInvoice({ ...invoice, paymentMode: selectedOption });
  };

  const handleMedicineSelect = (index, selectedMedicine) => {
    const updatedItems = [...items];
    updatedItems[index].item = selectedMedicine;
    updatedItems[index].amt = selectedMedicine ? selectedMedicine.selling_price : "";
    updatedItems[index].qty = 1;
    updatedItems[index].discount = 0;
    updatedItems[index].total = selectedMedicine ? selectedMedicine.selling_price : 0;
    setItems(updatedItems);
  };

  const handleItemChange = (index, field, value) => {
  const updatedItems = [...items];
  updatedItems[index][field] = value;

  const qty = parseFloat(updatedItems[index].qty) || 0;
  const amt = parseFloat(updatedItems[index].amt) || 0;
  const discount = parseFloat(updatedItems[index].discount) || 0;

  // discount in %
  updatedItems[index].total = qty * amt * (1 - discount / 100);

  setItems(updatedItems);
};


  const addRow = () => {
    setItems([...items, { item: null, qty: 1, amt: "", discount: 0, total: 0 }]);
  };

  const removeRow = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  };

  const totalAmount = items.reduce((sum, item) => sum + (item.total || 0), 0);

  // ✅ Update Invoice
  const handleSubmit = async (e) => {
    e.preventDefault();
 
    try {
      setLoading(true);
      const payload = {
        full_name: invoice.fullName,
        phone: invoice.phone,
        address: invoice.address,
        date: invoice.date
  ? `${invoice.date.getFullYear()}-${String(
      invoice.date.getMonth() + 1
    ).padStart(2, "0")}-${String(invoice.date.getDate()).padStart(2, "0")}`
  : null,
        payment_mode: invoice.paymentMode?.value,
        total_amount: totalAmount,
        cash_amount: invoice.cashAmount || 0,
        upi_amount: invoice.upiAmount || 0,
        items: items.map((it) => ({
          medicine_id: it.item?.value,
          medicine_name: it.item?.label,
          qty: it.qty,
          amt: it.amt,
          discount: it.discount,
          total: it.total,
        })),
      };



      console.log("payload sent", payload);

      const res = await axios.put(`${baseurl}invoices/${id}`, payload);

      if (res.data.success) {
        Swal.fire({
          icon: "success",
          title: "Invoice Updated",
          text: "Invoice updated successfully!",
          timer: 1500,
          showConfirmButton: false,
        });
        navigate("/all-invoice");
      } else {
        Swal.fire("Error", res.data.message || "Failed to update invoice", "error");
      }
    } catch (err) {
      console.error("Error updating invoice:", err);
      Swal.fire("Error", "Something went wrong while updating!", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper bg-light">
        <div className="container py-4">
          <div className="card border-0 shadow-lg rounded-4">
            <div className="card-header rounded-top-4">
              <h4 className="mb-0 fw-bold d-flex align-items-center gap-2">
                <FaShoppingCart /> Edit Invoice #{id}
              </h4>
            </div>

            <div className="card-body p-4">
              {loading ? (
                <p className="text-center py-4">Loading invoice...</p>
              ) : (
                <form onSubmit={handleSubmit}>
                  {/* Customer Info */}
                  <div className="row g-3 mb-4">
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">
                        <FaUser className="me-2 text-primary" />
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={invoice.fullName}
                        onChange={handleInputChange}
                        className="form-control"
                        required
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label fw-semibold">
                        <FaPhone className="me-2 text-primary" />
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={invoice.phone}
                        onChange={handleInputChange}
                        className="form-control"
                        required
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label fw-semibold">
                        <FaCalendarAlt className="me-2 text-primary" />
                        Date
                      </label>
                      <Flatpickr
                        value={invoice.date}
                        onChange={handleDateChange}
                        options={{ dateFormat: "Y-m-d" }}
                        className="form-control"
                      />
                    </div>

                    <div className="col-md-8">
                      <label className="form-label fw-semibold">
                        <FaMapMarkerAlt className="me-2 text-primary" />
                        Address
                      </label>
                      <textarea
                        name="address"
                        value={invoice.address}
                        onChange={handleInputChange}
                        className="form-control"
                        rows="2"
                      ></textarea>
                    </div>

                    {/* Payment Mode + Split fields */}
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">
                        <FaRupeeSign className="me-2 text-primary" />
                        Payment Mode
                      </label>
                      <Select
                        options={paymentOptions}
                        value={invoice.paymentMode}
                        onChange={handleSelectChange}
                        styles={customSelectStyles}
                      />
                    </div>

                    {invoice.paymentMode?.value === "cash+upi" && (
                      <>
                        <div className="col-md-2">
                          <label className="form-label fw-semibold">
                            Cash Amount
                          </label>
                          <input
                            type="number"
                            name="cashAmount"
                            value={invoice.cashAmount}
                            onChange={handleInputChange}
                            className="form-control"
                            required
                          />
                        </div>
                        <div className="col-md-2">
                          <label className="form-label fw-semibold">
                            UPI Amount
                          </label>
                          <input
                            type="number"
                            name="upiAmount"
                            value={invoice.upiAmount}
                            onChange={handleInputChange}
                            className="form-control"
                            required
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* Items Table */}
                  <div className="table-responsive">
                    <table className="table table-bordered align-middle">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Qty</th>
                          <th>Amount</th>
                          <th>Discount %</th>
                          <th>Total</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((row, index) => (
                          <tr key={index}>
                            <td style={{ minWidth: "200px" }}>
                              <Select
                                options={medicineOptions}
                                value={row.item}
                                onChange={(val) => handleMedicineSelect(index, val)}
                                styles={customSelectStyles}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                min="1"
                                value={row.qty}
                                onChange={(e) =>
                                  handleItemChange(index, "qty", e.target.value)
                                }
                                className="form-control text-center"
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={row.amt}
                                onChange={(e) =>
                                  handleItemChange(index, "amt", e.target.value)
                                }
                                className="form-control text-center"
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={row.discount}
                                onChange={(e) =>
                                  handleItemChange(index, "discount", e.target.value)
                                }
                                className="form-control text-center"
                              />
                            </td>
                            <td className="fw-semibold text-center">
                              ₹{row.total.toFixed(2)}
                            </td>
                            <td className="text-center">
                              {items.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => removeRow(index)}
                                >
                                  <FaTrashAlt />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="d-flex justify-content-between align-items-center">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={addRow}
                    >
                      <FaPlusCircle className="me-1" /> Add Item
                    </button>

                    <h5 className="fw-bold">
                      Total:{" "}
                      <span className="text-success">₹{totalAmount.toFixed(2)}</span>
                    </h5>
                  </div>

                  <div className="text-end mt-4">
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg px-5 py-2"
                      disabled={loading}
                    >
                      💾 {loading ? "Updating..." : "Update Invoice"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default EditInvoice;
